import React from "react";
import {
  Menu,
  MenuItem,
  styled,
  Select,
  FormControl,
  InputLabel,
  Button,
  Slider,
  Typography,
  Box,
} from "@mui/material";

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1px solid #323C48",
    marginTop: "8px",
    maxWidth: "320px",
    padding: "16px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
    fontFamily: "Inter, sans-serif",
  },
});

const StyledSelect = styled(Select)({
  backgroundColor: "#fff",
  borderRadius: "8px",
  fontFamily: "Inter, sans-serif",
  fontSize: "14px",
  fontWeight: "600",
  color: "#222830",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E5E7EB",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ABB7C4",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#407097",
  },
});

const StyledButton = styled(Button)({
  textTransform: "none",
  padding: "8px 16px",
  borderRadius: "24px",
  fontWeight: 600,
  fontSize: "14px",
  fontFamily: "Inter, sans-serif",
  width: "96px",
  "&.MuiButton-contained": {
    backgroundColor: "#407097",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#ABB7C4",
    },
  },
  "&.MuiButton-outlined": {
    borderColor: "#323C48",
    color: "#323C48",
    "&:hover": {
      backgroundColor: "rgba(50, 60, 72, 0.04)",
    },
  },
});

const StyledFormControl = styled(FormControl)({
  "& .MuiInputLabel-root": {
    color: "#323C48",
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: "600",
    "&.Mui-focused": {
      color: "#323C48",
    },
  },
  marginBottom: "16px",
});

const StyledMenuItem = styled(MenuItem)({
  fontFamily: "Inter, sans-serif",
  fontSize: "14px",
  fontWeight: "600",
  color: "#222830",
});

const StyledSlider = styled(Slider)({
  color: "#407097",
  height: 8,
  padding: "13px 0",
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#407097",
  },
  "& .MuiSlider-rail": {
    opacity: 0.25,
    backgroundColor: "#407097",
  },
  "& .MuiSlider-thumb": {
    height: 27,
    width: 27,
    backgroundColor: "#fff",
    border: "2px solid currentColor",
    "&:hover": {
      boxShadow: "0 0 0 8px rgba(64, 112, 151, 0.16)",
    },
    "&.Mui-active": {
      boxShadow: "0 0 0 12px rgba(64, 112, 151, 0.16)",
    },
  },
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "600",
    top: -6,
    backgroundColor: "#407097",
    "&:before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
    },
  },
});

export const filterFunctions = {
  messageStatus: (lead, value) =>
    value === "all" ? true : lead.missedMassages > 0,

  role: (lead, value) =>
    value === "all"
      ? true
      : value === "both"
      ? lead.role === "B & S"
      : value === "buyer"
      ? lead.role === "Buyer" || lead.role === "B & S"
      : lead.role === "Seller" || lead.role === "B & S",

  notification: (lead, value) => {
    if (value === "all") return true;
    const hasReminder = Boolean(lead?.notification) && Boolean(lead?.parsedNotificTime);
    return value === "with" ? hasReminder : !hasReminder;
  },

  responseType: (lead, value) =>
    value === "all" ? true : lead.answerType === value,

  score: (lead, [min, max]) => {
    const score = parseFloat(lead.rating);
    return score >= min && score <= max;
  },

  // Treat Lead Type as source system (externalSource)
  leadType: (lead, value) => {
    if (value === "all") return true;
    const src = String(lead?.externalSource || lead?.source || '').toLowerCase();
    const wanted = String(value).toLowerCase();
    return src === wanted;
  },
};

export const applyFilters = (lead, filters) => {
  return (
    filterFunctions.messageStatus(lead, filters.messageStatus) &&
    filterFunctions.role(lead, filters.role) &&
    filterFunctions.notification(lead, filters.notification) &&
    filterFunctions.responseType(lead, filters.responseType) &&
    filterFunctions.score(lead, filters.score) &&
    filterFunctions.leadType(lead, filters.leadType)
  );
};

const Filters = ({
  anchorEl,
  open,
  onClose,
  tempFilters,
  onFilterChange,
  onSave,
}) => {
  const handleSelectChange = (filterType) => (event) => {
    onFilterChange(filterType, event.target.value);
  };

  const handleScoreChange = (event, newValue) => {
    onFilterChange("score", newValue);
  };

  return (
    <StyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <div className="p-2">
        <StyledFormControl fullWidth size="small">
          <InputLabel>Message Status</InputLabel>
          <StyledSelect
            value={tempFilters.messageStatus}
            label="Message Status"
            onChange={handleSelectChange("messageStatus")}
          >
            <StyledMenuItem value="all">All Messages</StyledMenuItem>
            <StyledMenuItem value="unread">Unread Messages</StyledMenuItem>
          </StyledSelect>
        </StyledFormControl>

        <StyledFormControl fullWidth size="small">
          <InputLabel>Customer Type</InputLabel>
          <StyledSelect
            value={tempFilters.role}
            label="Customer Type"
            onChange={handleSelectChange("role")}
          >
            <StyledMenuItem value="all">All Customer Types</StyledMenuItem>
            <StyledMenuItem value="buyer">Buyers</StyledMenuItem>
            <StyledMenuItem value="seller">Sellers</StyledMenuItem>
            <StyledMenuItem value="both">Buyers & Sellers</StyledMenuItem>
          </StyledSelect>
        </StyledFormControl>

        <StyledFormControl fullWidth size="small">
          <InputLabel>Response Type</InputLabel>
          <StyledSelect
            value={tempFilters.responseType}
            label="Response Type"
            onChange={handleSelectChange("responseType")}
          >
            <StyledMenuItem value="all">All Types</StyledMenuItem>
            <StyledMenuItem value="Suggested">Suggested</StyledMenuItem>
            <StyledMenuItem value="Manual">Manual</StyledMenuItem>
            <StyledMenuItem value="Autopilot">Autopilot</StyledMenuItem>
          </StyledSelect>
        </StyledFormControl>

        <StyledFormControl fullWidth size="small">
          <InputLabel>Lead Type</InputLabel>
          <StyledSelect
            value={tempFilters.leadType}
            label="Lead Type"
            onChange={handleSelectChange("leadType")}
          >
            <StyledMenuItem value="all">All Types</StyledMenuItem>
            <StyledMenuItem value="follow-up boss">Follow-Up Boss</StyledMenuItem>
          </StyledSelect>
        </StyledFormControl>

        <StyledFormControl fullWidth size="small">
          <InputLabel>Reminder</InputLabel>
          <StyledSelect
            value={tempFilters.notification}
            label="Reminder"
            onChange={handleSelectChange("notification")}
          >
            <StyledMenuItem value="all">All</StyledMenuItem>
            <StyledMenuItem value="with">With Reminder</StyledMenuItem>
            <StyledMenuItem value="without">Without Reminder</StyledMenuItem>
          </StyledSelect>
        </StyledFormControl>

        <StyledFormControl fullWidth size="small">
          <Typography
            gutterBottom
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "14px",
              fontWeight: "600",
              color: "#222830",
              mb: 2,
            }}
          >
            Customer Score Range
          </Typography>
          <Box sx={{ px: 1, pt: 1 }}>
            <StyledSlider
              getAriaLabel={() => "Customer score range"}
              value={tempFilters.score || [0, 10]}
              onChange={handleScoreChange}
              valueLabelDisplay="auto"
              getAriaValueText={(value) => `${value}`}
              min={0}
              max={10}
              step={0.1}
              marks={[
                { value: 0, label: "0" },
                { value: 5, label: "5" },
                { value: 10, label: "10" },
              ]}
            />
          </Box>
        </StyledFormControl>

        <div className="flex justify-end gap-x-2 mt-4">
          <StyledButton variant="outlined" onClick={onClose}>
            Cancel
          </StyledButton>
          <StyledButton variant="contained" onClick={onSave}>
            Save
          </StyledButton>
        </div>
      </div>
    </StyledMenu>
  );
};

export default Filters;
